import type { Column } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { Icon, Tooltip } from '~/components';
import { cn } from '~/lib/cn';

interface SortableIndicatorProps<T> {
  column: Column<T>;
  tooltip?: string;
  className?: string;
}

export function SortableIndicator<T>({
  column,
  tooltip,
  className,
}: SortableIndicatorProps<T>) {
  const { t } = useTranslation('components', {
    keyPrefix: 'sortableIndicator',
  });
  const sortDirection = column.getIsSorted();
  let iconType = 'arrow-up-arrow-down';

  if (sortDirection) {
    iconType = sortDirection === 'asc' ? 'arrow-up' : 'arrow-down';
  }

  const iconVariant = sortDirection ? 'solid' : 'light';

  return tooltip ? (
    <Tooltip
      content={<span className="font-semibold">{tooltip}</span>}
      delayDuration={0}
      sideOffset={16}
      side="top"
      align="end"
      size="md"
    >
      <span className={cn('w-md', className)}>
        <Icon
          type={iconType}
          variant={iconVariant}
          aria-label={t('iconLabel', {
            columnId: column.id,
          })}
          size="sm"
          onClick={column.getToggleSortingHandler()}
        />
      </span>
    </Tooltip>
  ) : (
    <span className={cn('flex w-md items-center justify-center', className)}>
      <Icon
        type={iconType}
        variant={iconVariant}
        aria-label={t('iconLabel', {
          columnId: column.id,
        })}
        size="sm"
        className={cn('cursor-pointer p-xs', {
          'text-default': sortDirection,
        })}
        onClick={column.getToggleSortingHandler()}
      />
    </span>
  );
}

SortableIndicator.displayName = 'SortableIndicator';
